// Modules
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

// CSS and assets
import './style.scss';
import './components/OrderReview/style.scss';

// Hooks
import { useAppDispatch, useAppSelector } from '@global/store/hooks';
import { useAuth } from '@global/hooks/useAuth';
import { useModal } from '@global/hooks/useModal';
import { useNotification } from '@global/hooks/useNotification';
import { triggerAEMHeaderCartIcon } from '@global/hooks/useAEMResource/utils';

// Components
import {
  SecurePayment,
  SubscriptionNotificationBody,
  InfoSection,
  SchoolStep,
  OrderReview,
  Title,
  BillingInfoSummary,
} from './components';

import { OrderSummary, buildOrderSummaryData } from './components/OrderSummary';
import { Spinner } from '@global/components/UI Elements/Spinner';

// Actions
import {
  setCartId,
  clearCartStates,
  setUpdateBillingInformation,
} from '@global/store/slices/cart/cartSlice';

import { cartSelector } from '@global/store/slices/cart/selectors';
import { getDeliveryOptions } from '@global/store/slices/cart/extraReducers/getDeliveryOptions';

// step 2
import { getCartData } from '@global/store/slices/cart/extraReducers/getCartData';
// -----------------------------

// Utils
import analytic from '@global/utils/AdobeAnalytics';
import { waitForAdobeAnalytics } from '@global/utils/waitForAdobeAnalytics';
import { prefixPath } from '@global/localization/services';

// Types
import { STATUSES } from '@pages/checkout/types';
import { AddToCartLayerType } from '@global/utils/AdobeAnalytics/types';

// Constants
import { NotificationTypes } from '@global/components/Notification';
import { LOCALE_CODES } from '@global/types/types';
import * as Constants from '@global/App/constants/constants';
import { getSubscriptionsForCheckout } from '@src/global/store/slices/cart/extraReducers/getSubscriptionsForCheckout';
import {
  CHECKOUT_ACCOUNT_STEP,
  CHECKOUT_PAYMENT_AND_BILLING_STEP,
  CHECKOUT_REVIEW_STEP,
  CHECKOUT_SCHOOL_STEP,
  CHECKOUT_SHIPPING_STEP,
} from '@pages/checkout/consts';
import { countriesSelector } from '@global/store/slices/country/countrySelector';
import {
  createCartUidCookieKey,
  eraseCartCookies,
  getCookieValue,
} from '@global/utils/cookie';
import { isCartHasUnavailableProducts } from '@global/store/slices/cart/extraReducers/isCartHasUnavailableProducts';
import { ShippingInformation } from './components/ShippingInformation';
import { ShippingInfoFormValuesType } from './components/ShippingInformation/types';
import { updateShippingInformation } from '@global/store/slices/cart/extraReducers/updateShippingInformation';
import { ShippingSummary } from './components/ShippingInformation/ShippingSummary';
import { SiteConfig } from '@global/utils/SiteConfig';

export const Checkout = () => {
  const { t, i18n } = useTranslation();
  const { push } = useHistory();
  const language = i18n.language as LOCALE_CODES;

  const dispatch = useAppDispatch();

  const cartState = useAppSelector(cartSelector);
  const countries = useAppSelector(countriesSelector);
  const shippingTitleRef = useRef<HTMLElement>(null);

  const withSchoolStep = useMemo(
    () => cartState.isTextbook && language === 'en-us',
    [cartState.isTextbook]
  );

  const withShippingStep = cartState.isPhysical;

  const availableSteps = useMemo(() => {
    return [
      CHECKOUT_ACCOUNT_STEP,
      withShippingStep ? CHECKOUT_SHIPPING_STEP : undefined,
      withSchoolStep ? CHECKOUT_SCHOOL_STEP : undefined,
      CHECKOUT_PAYMENT_AND_BILLING_STEP,
      CHECKOUT_REVIEW_STEP,
    ].filter(Boolean);
  }, [withShippingStep, withSchoolStep]);

  const shouldWaitForSubscriptionCheck = useMemo(
    () =>
      cartState.isSubscription &&
      cartState.updatedWithNewCustomer === STATUSES.TRUE &&
      cartState.isCheckSubscriptions !== STATUSES.TRUE,
    [
      cartState.isCheckSubscriptions,
      cartState.isSubscription,
      cartState.updatedWithNewCustomer,
    ]
  );

  const getStepNumber = (step: number) => {
    const position =
      availableSteps.findIndex((availableStep) => availableStep === step) + 1;
    return `${position}.`;
  };

  const { user, isLoggedIn, doLogout } = useAuth();
  const { setShowNotification } = useNotification();

  const { handleOpen: handleOpenModal } = useModal({
    content: () => (
      <SubscriptionNotificationBody email={user.email} doLogout={doLogout} />
    ),
    blockScreen: true,
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // View
  const [checkoutStep, setCheckoutStep] = useState<number>(
    CHECKOUT_SHIPPING_STEP
  );

  const redirectTo404 = useCallback(() => {
    const redirectPath = prefixPath(Constants.Pages.ERR404, language);
    push(redirectPath);
  }, []);

  const onOrderConfirmed = useCallback(() => {
    dispatch(clearCartStates());

    const { siteId, locale } = SiteConfig.getConfig();
    eraseCartCookies(locale, siteId);
    triggerAEMHeaderCartIcon();
  }, []);

  const editStepHandler = (step: number) => {
    switch (step) {
      case CHECKOUT_SCHOOL_STEP:
        setCheckoutStep(CHECKOUT_SCHOOL_STEP);
        break;
      case CHECKOUT_PAYMENT_AND_BILLING_STEP:
        setCheckoutStep(CHECKOUT_PAYMENT_AND_BILLING_STEP);
        break;
      case CHECKOUT_REVIEW_STEP:
        setCheckoutStep(CHECKOUT_REVIEW_STEP);
        break;
      case CHECKOUT_SHIPPING_STEP:
        setCheckoutStep(CHECKOUT_SHIPPING_STEP);
        break;
    }
  };

  const readyForModal = useMemo(
    () => cartState.isCartReady && !shouldWaitForSubscriptionCheck,
    [cartState.isCartReady, shouldWaitForSubscriptionCheck]
  );

  const modalOpenedRef = useRef(false);

  useEffect(() => {
    if (!readyForModal) return;

    if (
      !modalOpenedRef.current &&
      cartState.isSubscriptionExist &&
      cartState.isSubscription
    ) {
      handleOpenModal();
      modalOpenedRef.current = true;
      return;
    }

    if (!modalOpenedRef.current) {
      if (withShippingStep) {
        editStepHandler(CHECKOUT_SHIPPING_STEP);
        return;
      }

      if (withSchoolStep) {
        editStepHandler(CHECKOUT_SCHOOL_STEP);
        return;
      }

      editStepHandler(CHECKOUT_PAYMENT_AND_BILLING_STEP);
    }
  }, [
    readyForModal,
    cartState.isSubscriptionExist,
    cartState.isSubscription,
    withSchoolStep,
    withShippingStep,
    handleOpenModal,
  ]);

  // get Subscriptions before 2 step
  useEffect(() => {
    if (
      cartState.isSubscription &&
      cartState.updatedWithNewCustomer === STATUSES.TRUE &&
      cartState.isCheckSubscriptions === STATUSES.FALSE
    ) {
      dispatch(getSubscriptionsForCheckout({ uId: user?.uid }));
    }
  }, [cartState.updatedWithNewCustomer]);

  useEffect(() => {
    cartState.error &&
      setShowNotification([NotificationTypes.error, cartState.error]);
  }, [cartState.error]);

  const startCartFlow = async () => {
    const { siteId, locale } = SiteConfig.getConfig();
    const cartIdCookieKey = createCartUidCookieKey(locale, siteId);
    const cartIdCookieValue = getCookieValue(cartIdCookieKey);

    if (!cartIdCookieValue) {
      redirectTo404();
      return;
    }

    dispatch(setCartId(cartIdCookieValue));
    dispatch(getCartData({ language }));
    dispatch(getDeliveryOptions({ uid: cartIdCookieValue }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    startCartFlow();
  }, []);

  useEffect(() => {
    countries.length && dispatch(isCartHasUnavailableProducts());
  }, [countries]);

  // Fire pageView event when checkout page loads
  useEffect(() => {
    if (cartState.isCartReady) {
      waitForAdobeAnalytics(() => {
        analytic.dataLayerPageLoaded({
          page: {
            name: 'checkout:account completion',
            section: 'checkout',
            subSection: 'account completion',
          },
          event: 'pageView',
          isLoggedIn: !cartState.isGuestOrder && isLoggedIn,
        });
      });
    }
  }, [cartState.isCartReady, cartState.isGuestOrder, isLoggedIn]);

  const addToCartLayerProps: AddToCartLayerType = useMemo(
    () => ({
      product: cartState.orderItems?.[0],
      currency: cartState.orderItems?.[0]?.price.units,
    }),
    [cartState.orderItems?.length]
  );

  useEffect(() => {
    const shippingStep =
      withShippingStep && checkoutStep === CHECKOUT_SHIPPING_STEP;
    const schoolStep =
      !withShippingStep &&
      withSchoolStep &&
      checkoutStep === CHECKOUT_SCHOOL_STEP;
    const paymentStep =
      !withShippingStep &&
      !withSchoolStep &&
      checkoutStep === CHECKOUT_PAYMENT_AND_BILLING_STEP;
    if (
      (shippingStep || schoolStep || paymentStep) &&
      addToCartLayerProps?.product?.maId
    ) {
      waitForAdobeAnalytics(() => {
        analytic.dataLayerCheckOutPageLoad();
      });
    }
  }, [addToCartLayerProps, checkoutStep]);

  const selectedDeliveryOption = cartState.deliveryOptions?.find(
    ({ id }) => id === cartState.wSelectedDeliveryOption?.wdoCode
  );

  const orderSummaryData = buildOrderSummaryData({
    language,
    orderPrice: cartState.orderPrice,
    withTax: checkoutStep === CHECKOUT_REVIEW_STEP,
    withShipping: checkoutStep !== CHECKOUT_SHIPPING_STEP,
    deliveryOption: selectedDeliveryOption,
  });

  const handleSaveShippingInformation = async (
    formData: ShippingInfoFormValuesType
  ) => {
    await dispatch(updateShippingInformation(formData));
  };

  const handleShippingFormSubmit = async (
    formData: ShippingInfoFormValuesType
  ) => {
    await handleSaveShippingInformation(formData);
    editStepHandler(
      withSchoolStep ? CHECKOUT_SCHOOL_STEP : CHECKOUT_PAYMENT_AND_BILLING_STEP
    );
  };

  const handleShippingFormSelectCountry = (country: string) => {
    dispatch(getDeliveryOptions({ uid: cartState.cartId, country }));
  };

  const handleBackToTheShippingStep = () => {
    dispatch(setUpdateBillingInformation(STATUSES.FALSE));
    editStepHandler(CHECKOUT_SHIPPING_STEP);
    if (shippingTitleRef.current) {
      window.scrollTo(0, shippingTitleRef.current.offsetTop);
    }
  };

  return (
    <>
      {(shouldWaitForSubscriptionCheck ||
        cartState.isShowSpinner ||
        cartState.isLoading ||
        !countries.length) && <Spinner size={'64px'} withOverlay />}
      <div className="checkout">
        <div className="checkout__container">
          <div className="checkout__steps">
            <div className="checkout__header">
              <h1 className="checkout__header__title">{t('checkout.title')}</h1>
              <p className="checkout__header__subtitle">
                {t('checkout.requiredFields')}
              </p>
            </div>

            {/* CHECKOUT STEP 1 */}
            <Title
              numberText={getStepNumber(CHECKOUT_ACCOUNT_STEP)}
              title={t('checkout.step1.title')}
              current={CHECKOUT_ACCOUNT_STEP}
              active={checkoutStep}
              isEditHidden
            >
              <div className="checkout__selected-values regularText">
                {cartState.isGuestOrder
                  ? t('checkout.step1.guest')
                  : user?.email}
              </div>
            </Title>

            {withShippingStep ? (
              <>
                <Title
                  ref={shippingTitleRef}
                  numberText={getStepNumber(CHECKOUT_SHIPPING_STEP)}
                  title={t('checkout.shippingAddress.title')}
                  current={CHECKOUT_SHIPPING_STEP}
                  active={checkoutStep}
                  onClick={() => {
                    editStepHandler(CHECKOUT_SHIPPING_STEP);
                  }}
                >
                  <ShippingSummary
                    shippingInfo={cartState.shippingInfo}
                    locale={language}
                  />
                </Title>
                {checkoutStep === CHECKOUT_SHIPPING_STEP && (
                  <ShippingInformation
                    onSave={handleSaveShippingInformation}
                    isLoading={false}
                    onSubmit={handleShippingFormSubmit}
                    onSelectCountry={handleShippingFormSelectCountry}
                  />
                )}
              </>
            ) : null}

            {/* CHECKOUT STEP 2 */}
            {withSchoolStep ? (
              <SchoolStep
                stepNumber={getStepNumber(CHECKOUT_SCHOOL_STEP)}
                activeStep={checkoutStep}
                handleNextStep={() => {
                  dispatch(setUpdateBillingInformation(STATUSES.FALSE));
                  editStepHandler(CHECKOUT_PAYMENT_AND_BILLING_STEP);
                }}
                handleReturnToCurrentStep={() =>
                  editStepHandler(CHECKOUT_SCHOOL_STEP)
                }
              />
            ) : null}

            {/* CHECKOUT STEP 2 or 3 */}
            {checkoutStep === CHECKOUT_PAYMENT_AND_BILLING_STEP && (
              <div id="securePayment"></div>
            )}
            <Title
              numberText={getStepNumber(CHECKOUT_PAYMENT_AND_BILLING_STEP)}
              title={t('checkout.step2.title')}
              current={CHECKOUT_PAYMENT_AND_BILLING_STEP}
              active={checkoutStep}
              onClick={() => {
                dispatch(setUpdateBillingInformation(STATUSES.FALSE));
                editStepHandler(CHECKOUT_PAYMENT_AND_BILLING_STEP);
                dispatch(getCartData({ language }));
              }}
            >
              <BillingInfoSummary
                billingInfo={cartState.billingInfo}
                paymentInfo={cartState.paymentInfo}
              />
            </Title>
            {checkoutStep === CHECKOUT_PAYMENT_AND_BILLING_STEP && (
              <SecurePayment
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                handleOpenModal={handleOpenModal}
                onSubmit={() => editStepHandler(CHECKOUT_REVIEW_STEP)}
              />
            )}

            {/* CHECKOUT STEP 3 or 4 */}
            <Title
              numberText={getStepNumber(CHECKOUT_REVIEW_STEP)}
              title={t('checkout.review.title')}
              current={CHECKOUT_REVIEW_STEP}
              active={checkoutStep}
              onClick={() => {
                dispatch(setUpdateBillingInformation(STATUSES.FALSE));
                editStepHandler(CHECKOUT_REVIEW_STEP);
              }}
            />
            {checkoutStep === CHECKOUT_REVIEW_STEP && (
              <div className="order-review-step">
                <div className="order-review-desktop">
                  <OrderReview
                    deliveryOption={selectedDeliveryOption}
                    orderItems={cartState.orderItems}
                    orderPrice={cartState.orderPrice}
                    isLoading={isLoading}
                    isSummaryLoading={cartState.isLoading || !countries.length}
                    cartId={cartState.cartId}
                    setIsLoading={setIsLoading}
                    onSuccess={onOrderConfirmed}
                    backToTheShippingStep={handleBackToTheShippingStep}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="order-summary-desktop">
            <OrderSummary
              data={orderSummaryData}
              isShippingVisible={withShippingStep}
              discounts={cartState.discounts}
              isFreeTrial={cartState.isFreeTrial}
              loading={cartState.isLoading || !countries.length}
            />
          </div>
        </div>
      </div>
      <div className="static-info-section">
        <InfoSection />
      </div>
      <OrderSummary
        isMobile
        data={orderSummaryData}
        discounts={cartState.discounts}
        isFreeTrial={cartState.isFreeTrial}
        loading={cartState.isLoading || !countries.length}
        isShippingVisible={withShippingStep}
      />
    </>
  );
};
